/**
 * React Query hook for searching documents
 * Used for @ mention functionality in chat input
 */
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api-client";

export interface Document {
    document_id: string;
    file_name: string | null;
    document_type: string | null;
    document_type_id: string | null;
    document_number: string | null;
    document_date: string | null;
    container_id: string | null;
    file_mime_type: string | null;
    file_size_bytes: number | null;
    container: {
        container_number: string;
        container_type: string | null;
    } | null;
}

interface UseDocumentSearchOptions {
    query: string;
    companyId: string;
    enabled?: boolean;
    limit?: number;
}

export function useDocumentSearch({
    query,
    companyId,
    enabled = true,
    limit = 20,
}: UseDocumentSearchOptions) {
    const hasQuery = query.length >= 2;
    const effectiveLimit = hasQuery ? limit : 5; // Default to 5 for recent docs
    
    return useQuery({
        queryKey: ["documents", "search", query || "recent", companyId, effectiveLimit],
        queryFn: async (): Promise<Document[]> => {
            const params = new URLSearchParams({
                limit: effectiveLimit.toString(),
            });
            
            // Only add search param if query is provided and has at least 2 chars
            if (hasQuery) {
                params.append("search", query);
            }

            const response = await apiGet<Document[]>(
                `/documents/search?${params.toString()}`,
                { requireAuth: true }
            );

            return response;
        },
        enabled: enabled && !!companyId,
        staleTime: 30 * 1000, // 30 seconds
        gcTime: 5 * 60 * 1000, // 5 minutes
        retry: 1,
    });
}
